import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { combineDateTime, validateSchedule } from "@/lib/wib";

// Satu draft jadwal (WIB) untuk seluruh alur peminjam: katalog → detail →
// checkout. Disimpan di localStorage di bawah key khusus prototipe M2 —
// tidak menyentuh `gudang_cart_v2` milik keranjang. Edit selalu lewat draft
// lokal dialog; hanya Apply yang persist.
const KEY = "dexter.schedule.v1";

const EMPTY = { pickup_date: "", return_date: "", pickup_time: "09:00", return_time: "17:00" };

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...EMPTY };
    const p = JSON.parse(raw);
    return {
      pickup_date: typeof p.pickup_date === "string" ? p.pickup_date : "",
      return_date: typeof p.return_date === "string" ? p.return_date : "",
      pickup_time: /^\d{2}:\d{2}$/.test(p.pickup_time || "") ? p.pickup_time : "09:00",
      return_time: /^\d{2}:\d{2}$/.test(p.return_time || "") ? p.return_time : "17:00",
    };
  } catch {
    return { ...EMPTY };
  }
}

const ScheduleContext = createContext(null);

export function ScheduleProvider({ children }) {
  const [draft, setDraft] = useState(read);

  const persist = useCallback((next) => {
    setDraft(next);
    try {
      localStorage.setItem(KEY, JSON.stringify(next));
    } catch {
      // Storage penuh/diblokir: state sesi tetap jalan, tidak crash.
    }
  }, []);

  const clear = useCallback(() => {
    persist({ ...EMPTY });
  }, [persist]);

  const checked = useMemo(() => {
    const pickup = combineDateTime(draft.pickup_date, draft.pickup_time);
    const ret = combineDateTime(draft.return_date, draft.return_time);
    const v = validateSchedule(pickup, ret);
    return { pickup, ret, ...v };
  }, [draft]);

  const value = useMemo(() => ({
    draft,
    patch: (p) => persist({ ...draft, ...p }),
    clear,
    ...checked,
  }), [draft, persist, clear, checked]);

  return <ScheduleContext.Provider value={value}>{children}</ScheduleContext.Provider>;
}

export const useSchedule = () => useContext(ScheduleContext);
